/**
 * Region Component
 * 
 * Displays a musical region box with performance parameters, icons, and knobs.
 * Handles different views: standard, short, break, and expanded.
 */

import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Knob } from './Knob';
import { IconContainer } from './IconContainer';
import { KNOB_TYPES } from '../constants/regions';
import './Region.css';

/**
 * Arrow icons for multi-line regions
 */
function RightArrow() {
  return <img className="right-arrow" src="./right_arrow.png" alt="→" />;
}

function LeftArrow() {
  return <img className="left-arrow" src="./left_arrow.png" alt="←" />;
}

/**
 * Break region display (simplified)
 */
function BreakRegion({ arrow }) {
  const showRightArrow = arrow === 'r' || arrow === 'rl';
  const showLeftArrow = arrow === 'l' || arrow === 'rl';
  
  return (
    <div className="box break">
      {showLeftArrow && <LeftArrow />}
      <b>Break</b>
      {showRightArrow && <RightArrow />}
    </div>
  );
}

/**
 * Short region display (< 2 bars)
 */
function ShortRegion({ region, arrow, onClick }) {
  const showRightArrow = arrow === 'r' || arrow === 'rl';
  const showLeftArrow = arrow === 'l' || arrow === 'rl';
  const typeName = region.type.charAt(0).toUpperCase() + region.type.slice(1);
  
  return (
    <div className={`box ${region.type}`} onClick={onClick}>
      {showLeftArrow && <LeftArrow />}
      <b>{typeName}</b>
      <p />
      <h1 style={{ fontSize: '35px', fontWeight: '500' }}>!</h1>
      {showRightArrow && <RightArrow />}
    </div>
  );
}

/**
 * Knob container with all three knobs
 */
function KnobContainer({ region }) {
  const typeName = region.type.charAt(0).toUpperCase() + region.type.slice(1);
  
  return (
    <div className="knob-container">
      <b>{typeName}</b>
      <Knob label={KNOB_TYPES.DYNAMICS.label} range={region.knobs.dynamics} />
      <Knob label={KNOB_TYPES.COMPLEXITY.label} range={region.knobs.complexity} />
      <Knob label={KNOB_TYPES.FILLS.label} range={region.knobs.fills} />
    </div>
  );
}

/**
 * Standard region display
 */
function StandardRegion({ region, arrow }) {
  const showRightArrow = arrow === 'r' || arrow === 'rl';
  const showLeftArrow = arrow === 'l' || arrow === 'rl';
  
  return (
    <div className={`box ${region.type}`}>
      {showLeftArrow && <LeftArrow />}
      <KnobContainer region={region} />
      <IconContainer region={region} />
      {showRightArrow && <RightArrow />}
    </div>
  );
}

/**
 * Expanded region view (modal overlay)
 */
function ExpandedRegionView({ region, onClose }) {
  const barCount = region.end - region.start;
  
  return (
    <div className="expanded-background" onClick={onClose}>
      <div className="expanded-container">
        <p>{barCount} Bars</p>
        <div className="expanded-box">
          <p>Bar {region.start + 1}</p>
          <div className={`box ${region.type} expanded-region-width`}>
            <KnobContainer region={region} />
            <IconContainer region={region} />
          </div>
          <p>Bar {region.end + 1}</p>
        </div>
      </div>
    </div>
  );
}

/**
 * Main region component
 * 
 * @param {object} props - Component props
 * @param {object} props.region - Region data
 * @param {string} props.arrow - Arrow type ('r', 'l', 'rl', or null)
 * @param {boolean} props.isShort - Whether to display as short region
 */
export function Region({ region, arrow = null, isShort = false }) {
  const [isExpanded, setIsExpanded] = useState(false);
  
  const handleClick = () => {
    if (region.type !== 'break') {
      setIsExpanded(true);
    }
  };
  
  const handleClose = () => {
    setIsExpanded(false);
  };
  
  // Break regions have special display
  if (region.type === 'break') {
    return <BreakRegion arrow={arrow} />;
  }
  
  // Short regions show simplified view
  if (isShort) {
    return (
      <>
        <ShortRegion region={region} arrow={arrow} onClick={handleClick} />
        {isExpanded && createPortal(
          <ExpandedRegionView region={region} onClose={handleClose} />,
          document.body
        )}
      </>
    );
  }
  
  // Standard region display
  return (
    <>
      <div onClick={handleClick}>
        <StandardRegion region={region} arrow={arrow} />
      </div>
      {isExpanded && createPortal(
        <ExpandedRegionView region={region} onClose={handleClose} />,
        document.body
      )}
    </>
  );
}

